/**
 * NativeTable — renders an HTML <table> string as a proper native React Native grid.
 *
 * react-native-render-html renders every <td>/<th> as a block element (stacked),
 * so all table HTML is intercepted in MarkdownBody before reaching RNRH and routed
 * here instead. The result is a horizontally-scrollable native table that matches
 * the web's overflow-x:auto table style.
 */

import React, { useMemo, useState } from 'react';
import { View, Text, ScrollView, StyleSheet, type LayoutChangeEvent } from 'react-native';
import { useTheme } from '../hooks/useTheme';

// Minimum column width in pixels. Columns grow to fit content.
const COL_MIN_WIDTH = 80;
const COL_PADDING_H = 10;
const CHAR_WIDTH_APPROX = 7.5; // ~px per character at font-size 13

function stripHtml(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .trim();
}

function parseAlignment(thOrTd: string): 'left' | 'center' | 'right' {
  const m = thOrTd.match(/text-align:\s*(left|center|right)/i);
  return (m?.[1] as 'left' | 'center' | 'right') || 'left';
}

interface Cell {
  text: string;
  align: 'left' | 'center' | 'right';
}

interface TableData {
  headers: Cell[];
  rows: Cell[][];
}

function parseTableHtml(html: string): TableData {
  const headers: Cell[] = [];
  const rows: Cell[][] = [];

  // Extract all <th> from <thead>
  const theadM = html.match(/<thead>([\s\S]*?)<\/thead>/i);
  if (theadM) {
    const thRegex = /<th([^>]*)>([\s\S]*?)<\/th>/gi;
    let m: RegExpExecArray | null;
    while ((m = thRegex.exec(theadM[1])) !== null) {
      headers.push({ text: stripHtml(m[2]), align: parseAlignment(m[1]) });
    }
  }

  // Extract all <tr> from <tbody>
  const tbodyM = html.match(/<tbody>([\s\S]*?)<\/tbody>/i);
  if (tbodyM) {
    const trRegex = /<tr>([\s\S]*?)<\/tr>/gi;
    let tr: RegExpExecArray | null;
    while ((tr = trRegex.exec(tbodyM[1])) !== null) {
      const cells: Cell[] = [];
      const tdRegex = /<td([^>]*)>([\s\S]*?)<\/td>/gi;
      let td: RegExpExecArray | null;
      while ((td = tdRegex.exec(tr[1])) !== null) {
        cells.push({ text: stripHtml(td[2]), align: parseAlignment(td[1]) });
      }
      if (cells.length > 0) rows.push(cells);
    }
  }

  return { headers, rows };
}

function colWidth(headerText: string, colData: Cell[]): number {
  const allText = [headerText, ...colData.map((c) => c.text)];
  // Longest single line in this column (account for \n wrapped text)
  const maxLen = Math.max(
    ...allText.flatMap((t) => t.split('\n').map((l) => l.length))
  );
  return Math.max(COL_MIN_WIDTH, Math.ceil(maxLen * CHAR_WIDTH_APPROX) + COL_PADDING_H * 2);
}

interface Props {
  html: string;
}

export default function NativeTable({ html }: Props) {
  const theme = useTheme();
  const s = makeStyles(theme);

  const { headers, rows } = useMemo(() => parseTableHtml(html), [html]);

  // Available width from the parent — used to decide whether the
  // intrinsic column widths leave dead space to the right. When they
  // do, we distribute the remainder via `flexGrow` so the table
  // stretches edge-to-edge instead of hugging the left.
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const onContainerLayout = (e: LayoutChangeEvent) => {
    const w = e.nativeEvent.layout.width;
    if (w > 0 && w !== containerWidth) setContainerWidth(w);
  };

  if (headers.length === 0) return null;

  // Pre-calculate column widths based on content (these are minimums).
  const widths = headers.map((h, ci) =>
    colWidth(h.text, rows.map((r) => r[ci] ?? { text: '', align: 'left' }))
  );

  // Sum of intrinsic widths — when this is smaller than the available
  // container width, we stretch each cell with `flexGrow: 1` so the
  // remainder is distributed proportionally. When intrinsic >= container,
  // the cells keep their fixed widths and horizontal scroll handles
  // overflow as before.
  const totalIntrinsic = widths.reduce((a, b) => a + b, 0);
  const shouldStretch = containerWidth > 0 && totalIntrinsic < containerWidth;
  const cellWidthStyle = (w: number) =>
    shouldStretch
      ? { minWidth: w, flexGrow: 1, flexBasis: w }
      : { width: w };

  // Shadow lives on the OUTER wrapper (no `overflow: hidden`) so it
  // isn't clipped by the rounded-corner mask. Inner View carries the
  // radius + `overflow: hidden` to clip header/row backgrounds to the
  // pill shape.
  return (
    <View style={s.shadowWrapper} onLayout={onContainerLayout}>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={shouldStretch ? s.scrollContentStretch : undefined}
      >
        <View style={[s.tableClip, { backgroundColor: theme.colors.card }, shouldStretch && s.tableStretch]}>
          {/* Header row — clean band, no inter-cell borders. */}
          <View style={[s.row, s.headerRow, { backgroundColor: theme.colors.coreBackground, borderBottomColor: theme.colors.border }]}>
            {headers.map((h, ci) => (
              <View key={ci} style={[s.cell, s.headerCell, cellWidthStyle(widths[ci])]}>
                <Text
                  style={[s.headerText, { color: theme.colors.muted, textAlign: h.align }]}
                  numberOfLines={0}
                >
                  {h.text.toUpperCase()}
                </Text>
              </View>
            ))}
          </View>

          {/* Data rows — subtle zebra against card surface; separator-only
              between rows, no vertical cell rules. */}
          {rows.map((row, ri) => (
            <View
              key={ri}
              style={[
                s.row,
                ri !== rows.length - 1 && { borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: theme.colors.border },
                {
                  backgroundColor: ri % 2 === 1 ? theme.colors.coreBackground : 'transparent',
                },
              ]}
            >
              {headers.map((hdr, ci) => {
                const cell = row[ci] ?? { text: '', align: hdr.align };
                return (
                  <View key={ci} style={[s.cell, cellWidthStyle(widths[ci])]}>
                    <Text
                      style={[s.cellText, { color: theme.colors.headerText, textAlign: cell.align }]}
                      numberOfLines={0}
                    >
                      {cell.text}
                    </Text>
                  </View>
                );
              })}
            </View>
          ))}
        </View>
      </ScrollView>
    </View>
  );
}

const makeStyles = (theme: ReturnType<typeof import('../hooks/useTheme').useTheme>) =>
  StyleSheet.create({
    // Outer wrapper — carries the shadow and the rounded radius.
    // Crucially does NOT use `overflow: hidden`, so the drop-shadow
    // renders past the table's bounding box instead of being clipped
    // by the rounded clip mask (RN bug — overflow:hidden + shadow
    // never composes).
    shadowWrapper: {
      marginVertical: 12,
      marginHorizontal: 2,
      // Tightened radius (14 → 10) to match the chip-pill rhythm.
      borderRadius: 10,
      // Custom tighter shadow matching the li chip: y-offset 2,
      // radius 3, opacity 0.08 — sharper, closer drop than the
      // shadows.card preset.
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.08,
      shadowRadius: 3,
      elevation: 2,
    },
    // Inner table — `overflow: hidden` clips the header band /
    // zebra-row backgrounds to the rounded radius. Shadow lives on
    // the parent wrapper above.
    tableClip: {
      borderRadius: 10,
      overflow: 'hidden',
    },
    // When content is narrower than the container, stretch the
    // inner table to fill so cell `flexGrow` distributes the
    // remainder edge-to-edge.
    tableStretch: {
      width: '100%',
    },
    scrollContentStretch: {
      flexGrow: 1,
    },
    row: {
      flexDirection: 'row',
    },
    headerRow: {
      borderBottomWidth: 1,
    },
    cell: {
      paddingHorizontal: 14,
      paddingVertical: 10,
      justifyContent: 'flex-start',
    },
    headerCell: {
      paddingVertical: 12,
    },
    headerText: {
      fontSize: 11,
      fontWeight: '700',
      lineHeight: 16,
      letterSpacing: 0.5,
    },
    cellText: {
      fontSize: 13,
      lineHeight: 19,
    },
  });

